import { inject, Injectable } from '@angular/core';
import { EAnalyticsStatus, EMatchingType } from '@enums/campaign-analytics.enum';
import {
  IAnalyticsNode,
  ICampaignAnalytics,
  ICreatorAnalytics,
  ICtaKpiStatus,
  ICtaKpiStatusable,
} from '@interfaces/campaign-analytics.interface';
import { CampaignAnalyticsUtilitiesService } from '@utilities/campaign-analytics-utilities.service';
import { DateUtilitiesService } from '@utilities/date-utilities.service';
import { isSameDay, isWithinInterval } from 'date-fns';
import { ICampaignAnalyticsFilter } from './campaign-details.interface';

@Injectable({
  providedIn: 'root',
})
export class CampaignDetailsFilterService {
  private readonly dateUtils = inject(DateUtilitiesService);
  private readonly campaignAnalyticsUtilities = inject(CampaignAnalyticsUtilitiesService);

  public isFilterClear(campaignAnalyticsFilter: ICampaignAnalyticsFilter): boolean {
    return (
      campaignAnalyticsFilter.creatorIds.size === 0 &&
      campaignAnalyticsFilter.ctaTemplateIds.size === 0 &&
      campaignAnalyticsFilter.platformFeatures.size === 0 &&
      campaignAnalyticsFilter.executionStatuses.size === 0 &&
      campaignAnalyticsFilter.startDate === null &&
      campaignAnalyticsFilter.endDate === null
    );
  }

  public filterCampaignAnalytics(
    analyticsNodes: IAnalyticsNode[],
    campaignAnalytics: ICampaignAnalytics,
    campaignAnalyticsFilter: ICampaignAnalyticsFilter,
  ): [IAnalyticsNode[], ICampaignAnalytics] {
    const filteredAnalyticsNodes = this.filterAnalyticsNodes(analyticsNodes, campaignAnalyticsFilter);
    const filteredCampaignAnalytics = structuredClone(campaignAnalytics);
    this.filterCampaignAnalyticsCreators(filteredAnalyticsNodes, filteredCampaignAnalytics);

    this.recalculateCtaKpiStatusable(filteredAnalyticsNodes, filteredCampaignAnalytics);

    return [filteredAnalyticsNodes, filteredCampaignAnalytics];
  }

  public filterShellPlannedAnalyticsNodes(
    analyticsNodes: IAnalyticsNode[],
    campaignAnalyticsFilter: ICampaignAnalyticsFilter,
  ): IAnalyticsNode[] {
    return analyticsNodes.filter((analyticsNode) => {
      return (
        !this.isCreatorConditionFailed(analyticsNode, campaignAnalyticsFilter) &&
        !this.isDateConditionFailed(analyticsNode, campaignAnalyticsFilter) &&
        !this.isExecutionStatusConditionFailed(analyticsNode, campaignAnalyticsFilter) &&
        !this.isPlatformFeatureConditionFailed(analyticsNode, campaignAnalyticsFilter)
      );
    });
  }

  private filterAnalyticsNodes(
    analyticsNodes: IAnalyticsNode[],
    campaignAnalyticsFilter: ICampaignAnalyticsFilter,
  ): IAnalyticsNode[] {
    const originalAnalyticsNodes: IAnalyticsNode[] = structuredClone(analyticsNodes);
    const filteredAnalyticsNodes: IAnalyticsNode[] = [];

    for (const analyticsNode of originalAnalyticsNodes) {
      if (
        this.isCreatorConditionFailed(analyticsNode, campaignAnalyticsFilter) ||
        this.isPlatformFeatureConditionFailed(analyticsNode, campaignAnalyticsFilter) ||
        this.isExecutionStatusConditionFailed(analyticsNode, campaignAnalyticsFilter) ||
        this.isDateConditionFailed(analyticsNode, campaignAnalyticsFilter)
      ) {
        continue;
      }

      filteredAnalyticsNodes.push(analyticsNode);
    }

    return filteredAnalyticsNodes;
  }

  private filterCampaignAnalyticsCreators(
    analyticsNodes: IAnalyticsNode[],
    campaignAnalytics: ICampaignAnalytics,
  ): ICampaignAnalytics {
    const validCreatorIds = new Set(analyticsNodes.map((analyticsNode) => analyticsNode.creatorId));

    campaignAnalytics.creators = new Map(
      Array.from(campaignAnalytics.creators.entries()).filter(([creatorId]) => validCreatorIds.has(creatorId)),
    );

    return campaignAnalytics;
  }

  private updateCtaKpiStatusCounts(source: ICtaKpiStatus, targets: ICtaKpiStatusable[]): void {
    const {
      totalDeliverables,
      expectedDeliverables,
      currentProcessingDeliverables,
      currentOkDeliverables,
      currentProblemDeliverables,
      currentFailedDeliverables,
      currentBonusDeliverables,
      expectedKpis,
      currentProcessingKpis,
      currentOkKpis,
      currentProblemKpis,
      currentFailedKpis,
    } = source;

    targets.forEach((target) => {
      target.ctaKpiStatus.totalDeliverables += totalDeliverables;
      target.ctaKpiStatus.expectedDeliverables += expectedDeliverables;
      target.ctaKpiStatus.currentProcessingDeliverables += currentProcessingDeliverables;
      target.ctaKpiStatus.currentOkDeliverables += currentOkDeliverables;
      target.ctaKpiStatus.currentProblemDeliverables += currentProblemDeliverables;
      target.ctaKpiStatus.currentFailedDeliverables += currentFailedDeliverables;
      target.ctaKpiStatus.currentBonusDeliverables += currentBonusDeliverables;
      target.ctaKpiStatus.expectedKpis += expectedKpis;
      target.ctaKpiStatus.currentProcessingKpis += currentProcessingKpis;
      target.ctaKpiStatus.currentOkKpis += currentOkKpis;
      target.ctaKpiStatus.currentProblemKpis += currentProblemKpis;
      target.ctaKpiStatus.currentFailedKpis += currentFailedKpis;
    });
  }

  private resetCtaKpiStatus(target: ICtaKpiStatusable): void {
    target.ctaKpiStatus.totalDeliverables = 0;
    target.ctaKpiStatus.expectedDeliverables = 0;
    target.ctaKpiStatus.currentProcessingDeliverables = 0;
    target.ctaKpiStatus.currentOkDeliverables = 0;
    target.ctaKpiStatus.currentProblemDeliverables = 0;
    target.ctaKpiStatus.currentFailedDeliverables = 0;
    target.ctaKpiStatus.currentBonusDeliverables = 0;
    target.ctaKpiStatus.expectedKpis = 0;
    target.ctaKpiStatus.currentProcessingKpis = 0;
    target.ctaKpiStatus.currentOkKpis = 0;
    target.ctaKpiStatus.currentProblemKpis = 0;
    target.ctaKpiStatus.currentFailedKpis = 0;
  }

  private recalculateCtaKpiStatusable(analyticsNodes: IAnalyticsNode[], campaignAnalytics: ICampaignAnalytics): void {
    const creatorsArray = Array.from(campaignAnalytics.creators.values());
    this.resetCtaKpiStatusable([campaignAnalytics, ...creatorsArray]);

    this.recalculateCreatorCtaKpiStatusable(creatorsArray, analyticsNodes);
    this.recalculateCampaignCtaKpiStatusable(campaignAnalytics, creatorsArray);
  }

  private recalculateCreatorCtaKpiStatusable(
    creatorsArray: ICreatorAnalytics[],
    analyticsNodes: IAnalyticsNode[],
  ): void {
    creatorsArray.forEach((creatorAnalytics) => {
      const statusSet = new Set<EAnalyticsStatus>();
      const statusKpiSet = new Set<EAnalyticsStatus>();

      analyticsNodes.forEach((analyticsNode) => {
        if (analyticsNode.creatorId === creatorAnalytics.creatorId) {
          statusSet.add(analyticsNode.mergedCtaResult.status);
          statusKpiSet.add(analyticsNode.mergedCtaResult.statusKpi);
          this.updateCtaKpiStatusCounts(analyticsNode.mergedCtaResult.ctaKpiStatus, [creatorAnalytics]);
        }
      });

      this.campaignAnalyticsUtilities.calculateAnalyticsStatusAndColor(statusSet, creatorAnalytics, statusKpiSet);
    });
  }

  private recalculateCampaignCtaKpiStatusable(
    campaignAnalytics: ICampaignAnalytics,
    creatorsArray: ICreatorAnalytics[],
  ): void {
    campaignAnalytics.creators.forEach((creatorAnalytics) => {
      this.updateCtaKpiStatusCounts(creatorAnalytics.ctaKpiStatus, [campaignAnalytics]);
    });

    const [statusSet, statusKpiSet] = creatorsArray.reduce(
      (acc, creatorAnalytics) => {
        acc[0].add(creatorAnalytics.status);
        acc[1].add(creatorAnalytics.statusKpi);

        return acc;
      },
      [new Set<EAnalyticsStatus>(), new Set<EAnalyticsStatus>()],
    );

    this.campaignAnalyticsUtilities.calculateAnalyticsStatusAndColor(statusSet, campaignAnalytics, statusKpiSet);
  }

  private resetAnalyticsStatus(target: ICtaKpiStatusable): void {
    target.status = EAnalyticsStatus.ok;
    target.ctaKpiStatus.deliverableColor = 'var(--shikenso-success-base)';
  }

  private resetCtaKpiStatusable(targets: ICtaKpiStatusable[]): void {
    targets.forEach((target) => {
      this.resetCtaKpiStatus(target);
      this.resetAnalyticsStatus(target);
    });
  }

  private isCreatorConditionFailed(
    analyticsNode: IAnalyticsNode,
    campaignAnalyticsFilter: ICampaignAnalyticsFilter,
  ): boolean {
    return (
      !!campaignAnalyticsFilter.creatorIds.size && !campaignAnalyticsFilter.creatorIds.has(analyticsNode.creatorId)
    );
  }

  private isCtaConditionFailed(
    analyticsNode: IAnalyticsNode,
    campaignAnalyticsFilter: ICampaignAnalyticsFilter,
  ): boolean {
    return (
      !!campaignAnalyticsFilter.ctaTemplateIds.size &&
      !campaignAnalyticsFilter.ctaTemplateIds.has(analyticsNode.nonExecutedCtaId ?? '') &&
      !Array.from(analyticsNode.ctaIds).some((ctaId) => campaignAnalyticsFilter.ctaTemplateIds.has(ctaId))
    );
  }

  private isPlatformFeatureConditionFailed(
    analyticsNode: IAnalyticsNode,
    campaignAnalyticsFilter: ICampaignAnalyticsFilter,
  ): boolean {
    return (
      !!campaignAnalyticsFilter.platformFeatures.size &&
      !campaignAnalyticsFilter.platformFeatures.has(analyticsNode.platformFeature)
    );
  }

  private isExecutionStatusConditionFailed(
    analyticsNode: IAnalyticsNode,
    campaignAnalyticsFilter: ICampaignAnalyticsFilter,
  ): boolean {
    return (
      !!campaignAnalyticsFilter.executionStatuses.size &&
      !campaignAnalyticsFilter.executionStatuses.has(analyticsNode.executionStatus)
    );
  }

  private isDateConditionFailed(
    analyticsNode: IAnalyticsNode,
    campaignAnalyticsFilter: ICampaignAnalyticsFilter,
  ): boolean {
    const { startDate: filterStartDate, endDate: filterEndDate } = campaignAnalyticsFilter;

    if (!filterStartDate || !filterEndDate) {
      return false;
    }

    const { executionTime } = analyticsNode;
    const { plannedStartDate, plannedStartTime } = analyticsNode.plannedExecution ?? {};

    const plannedDate = plannedStartTime ?? plannedStartDate ?? null;
    const plannedDateObject = plannedDate ? this.dateUtils.parseDateTimeFromApi(plannedDate) : null;

    const executionDateObject = executionTime ? this.dateUtils.parseDateTimeFromApi(executionTime) : null;

    return (
      (plannedDateObject &&
        analyticsNode.executionStatus === EMatchingType.planned &&
        !isSameDay(plannedDateObject, filterStartDate) &&
        !isSameDay(plannedDateObject, filterEndDate) &&
        !isWithinInterval(plannedDateObject, { end: filterEndDate, start: filterStartDate })) ||
      (!!executionDateObject &&
        !isSameDay(executionDateObject, filterStartDate) &&
        !isSameDay(executionDateObject, filterEndDate) &&
        !isWithinInterval(executionDateObject, { end: filterEndDate, start: filterStartDate }))
    );
  }
}
